/**
 * How long a round gets, and why it gets less (SPEC-007 PR-7.6).
 *
 * A duel with no clock is a quiz a reader can sit and reason their way through:
 * power-to-weight, what the marque claimed, which of the two they have seen a
 * lap time for. That is a good way to spend thirty seconds and it is not a
 * game — nothing about it gets harder, so a long streak is a measure of patience
 * rather than of knowing these cars.
 *
 * The clock is what turns it into one. It starts generous enough to read two
 * cards and think, and it takes a second off every round, so a run ends when the
 * reader runs out of things they know INSTANTLY rather than when they run out of
 * things they can work out. The floor is where a person can still physically
 * read a question and tap: below that the game would stop being about cars and
 * start being about reaction time, which is the one thing this product has no
 * opinion about.
 *
 * Pure arithmetic over the round number. The ticking itself is a browser
 * concern and lives in `application/game` — this module never sees a timer, and
 * it does not know what a tick SOUNDS like either: it answers when one is owed
 * and how hard the clock is pressing, and `duelTick.js` turns that into a
 * noise.
 */

/** Long enough to read two names and think about them once. */
export const FIRST_ROUND_SECONDS = 15;

/**
 * The floor, and it is a floor rather than an asymptote for a reason.
 *
 * Five seconds is about the least a reader can be given and still read a
 * question, look at two cards and reach one — with a real phone, one thumb, and
 * no idea which question is coming. A run that kept shortening past it would
 * end on the tap being late rather than on the answer being wrong, and every
 * player would top out at the same round.
 */
export const FLOOR_SECONDS = 5;

/** A second a round, which is a curve a player can feel without being told. */
const STEP_SECONDS = 1;

/**
 * The allowance for a round, in seconds.
 *
 * Round 1 gets the full fifteen; round 11 and everything after it gets the
 * floor. Rounds are 1-based, and anything that is not a round number gets the
 * opening allowance rather than a NaN that would expire the moment it started.
 */
export function roundSeconds(round) {
    if (!Number.isFinite(round) || round < 1) {
        return FIRST_ROUND_SECONDS;
    }

    return Math.max(FLOOR_SECONDS, FIRST_ROUND_SECONDS - (Math.trunc(round) - 1) * STEP_SECONDS);
}

/**
 * How much of the allowance is left, between 0 and 1.
 *
 * The number the drain bar is drawn on. Separate from the seconds themselves
 * because the bar and the counter are two readings of one clock, and a bar
 * computed from a rounded second would visibly disagree with the digit beside
 * it at every step.
 */
export function remainingShare(secondsLeft, round) {
    const allowance = roundSeconds(round);

    if (!Number.isFinite(secondsLeft) || secondsLeft <= 0) {
        return 0;
    }

    return Math.min(secondsLeft / allowance, 1);
}

/** The last few seconds, where the clock stops being background and starts being the point. */
export const URGENT_SECONDS = 5;

export function isUrgent(secondsLeft) {
    return Number.isFinite(secondsLeft) && secondsLeft > 0 && secondsLeft <= URGENT_SECONDS;
}

/**
 * Where the clock becomes AUDIBLE, which is twice as early as it becomes urgent.
 *
 * The two windows are different questions and the answer to them is not the same
 * number. Urgency is about the SCREEN: below five seconds the digit and the bar
 * take the heat ramp, because that is the point at which a reader has to decide
 * whether to think or to guess. Ticking is about the ROOM: a clock that only
 * starts making a noise once you are already out of time is an alarm rather than
 * a clock, and it tells the reader nothing they could still act on.
 *
 * Ten seconds is where a tick starts being INFORMATION — long enough to hear it
 * building, short enough that it is not the soundtrack to the whole round. Below
 * round six the allowance is ten or less and the whole round ticks, which is
 * correct: by then the game is at full pressure from the moment it deals.
 */
export const TICKING_SECONDS = 10;

export function isTicking(secondsLeft) {
    return Number.isFinite(secondsLeft) && secondsLeft > 0 && secondsLeft <= TICKING_SECONDS;
}

/**
 * How hard the clock is pressing, from 0 at the top of the audible window to 1
 * on the last second.
 *
 * One continuous number rather than a flag per phase, because the thing being
 * asked for is a BUILD: the tick gets louder and higher all the way down, and
 * the change of voice at five seconds is a change of character laid over that
 * ramp rather than the ramp itself. Keeping it continuous is also what stops the
 * sound design from being a table of nine hand-tuned values that somebody has to
 * keep in step with the two constants above.
 *
 * Pure arithmetic, and deliberately no audio vocabulary in here: what a pressure
 * of 0.6 sounds like is `application/game/duelTick.js`'s business, and this
 * module has never known that a speaker exists.
 */
export function tickPressure(secondsLeft) {
    if (!isTicking(secondsLeft)) {
        return 0;
    }

    return (TICKING_SECONDS - secondsLeft) / (TICKING_SECONDS - 1);
}
